import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { usePageTitle } from "@/hooks/usePageTitle";
import { sakshi as SAKSHI_IMG, shailendra as SHAIL_IMG, candids as CANDIDS } from "@/config/founderAssets";

const fade = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0 } };

export default function Founders() {
  usePageTitle(
    "Founders — Sakshi & Shailendra",
    "Meet the founders of WeAutomate. Sakshi and Shailendra build applied-intelligence products across hardware, software, and the spaces businesses run in."
  );

  return (
    <div className="py-20">
      <div className="mx-auto max-w-5xl px-6 lg:px-10">

        {/* Opening */}
        <motion.div
          variants={fade} initial="hidden" animate="show"
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="text-[11px] uppercase tracking-widest text-white/40 font-mono">Founders</div>
          <h1 className="font-display text-4xl lg:text-6xl mt-5 tracking-tighter leading-[1.05] max-w-3xl">
            One of us thinks in{" "}
            <span className="gradient-text">circuits</span>.
            <br />
            The other thinks in stories.
          </h1>
          <p className="mt-5 text-xl text-white/70 leading-relaxed max-w-2xl">
            WeAutomate is what happens when they meet.
          </p>
        </motion.div>

        {/* Why we built this */}
        <motion.div
          variants={fade} initial="hidden" animate="show"
          transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="mt-16 max-w-2xl"
        >
          <div className="text-[11px] uppercase tracking-widest text-white/40 font-mono mb-4">Why we built this</div>
          <p className="text-white/80 leading-relaxed text-lg">
            An engineer and a designer-entrepreneur who believed India deserved world-class
            product engineering <em>and</em> design under one roof — without enterprise price tags.
          </p>
          <p className="mt-4 text-white/60 leading-relaxed">
            Magic and machinery, on purpose. That was the bet. It still is.
          </p>
        </motion.div>

        {/* Founders */}
        <div className="mt-20 space-y-20">

          {/* Sakshi */}
          <motion.div
            variants={fade} initial="hidden" whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="grid lg:grid-cols-5 gap-10 lg:gap-16 items-start"
          >
            {SAKSHI_IMG && (
              <div className="lg:col-span-2">
                <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-white/5">
                  <img src={SAKSHI_IMG} alt="Sakshi" className="w-full h-full object-cover" />
                </div>
              </div>
            )}
            <div className={SAKSHI_IMG ? "lg:col-span-3 pt-2" : "lg:col-span-5 pt-2"}>
              <div className="text-[11px] uppercase tracking-widest text-accent font-mono">Co-founder, Director &amp; CEO</div>
              <h2 className="font-display text-4xl mt-3 tracking-tight">Sakshi</h2>
              <div className="mt-6 space-y-4 text-white/70 leading-relaxed">
                <p>
                  Sakshi is the founder of WeStudio, the experiential-design studio behind
                  130+ projects across India, and a photographer who has spent her career
                  learning to see beyond the obvious frame.
                </p>
                <p>
                  She brings that eye for story, beauty, and detail to WeAutomate — where,
                  as Co-founder, Director &amp; CEO, she leads from the front, owns commercials
                  and client relationships, and makes sure everything we build carries a touch
                  of magic, not just a spec sheet.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Shailendra */}
          <motion.div
            variants={fade} initial="hidden" whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="grid lg:grid-cols-5 gap-10 lg:gap-16 items-start"
          >
            <div className={SHAIL_IMG ? "lg:col-span-3 pt-2 lg:order-first order-last" : "lg:col-span-5 pt-2"}>
              <div className="text-[11px] uppercase tracking-widest text-white/50 font-mono">Co-founder &amp; CTO</div>
              {/* Both founders display as first-name-only for visual parity
                  until Sakshi's surname is supplied (see FOUNDER TO SUPPLY
                  in PROJECT_LOG). Restore "Shailendra Pandav" then. */}
              <h2 className="font-display text-4xl mt-3 tracking-tight">Shailendra</h2>
              <div className="mt-6 space-y-4 text-white/70 leading-relaxed">
                <p>
                  Shailendra is a self-taught engineer who builds across the whole stack of
                  making real things — embedded firmware, RF, circuit boards, mechanical design,
                  applied ML/AI, and full-stack software.
                </p>
                <p>
                  He started with a degree in Geography and taught himself to ship hardware and
                  software from first principles. As Co-founder &amp; CTO, he leads WeAutomate's
                  technology and product engineering, turning ideas into things that actually
                  work and ship.
                </p>
              </div>
              <p className="mt-5 text-xs text-white/30 border-l-2 border-white/10 pl-3 leading-relaxed">
                Client IP — including work in RF and applied-intelligence domains — belongs
                to the respective clients. Shailendra presents capability only.
              </p>
            </div>
            {SHAIL_IMG && (
              <div className="lg:col-span-2">
                <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-white/5">
                  <img src={SHAIL_IMG} alt="Shailendra" className="w-full h-full object-cover" />
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {/* Shared goal */}
        <motion.div
          variants={fade} initial="hidden" whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-24 max-w-2xl"
        >
          <div className="text-[11px] uppercase tracking-widest text-white/40 font-mono mb-4">Our goal</div>
          <p className="text-2xl font-display tracking-tight text-white/90 leading-snug">
            India-first. Idea-to-shipped.
            <br />
            Engineering and design, under one roof.
            <br />
            <span className="text-white/50">Accessible to anyone with a real problem to solve.</span>
          </p>
        </motion.div>

        {/* Candid strip — hidden until real images are wired in founderAssets.js */}
        {CANDIDS.length > 0 && (
          <motion.div
            variants={fade} initial="hidden" whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-16"
          >
            <div className="text-[11px] uppercase tracking-widest text-white/30 font-mono mb-4">Behind the scenes</div>
            <div className="grid grid-cols-4 gap-2 rounded-2xl overflow-hidden">
              {CANDIDS.map((src, i) => (
                <div key={i} className="aspect-square overflow-hidden bg-white/5">
                  <img src={src} alt="" className="w-full h-full object-cover opacity-70 hover:opacity-90 transition-opacity" />
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* CTA */}
        <motion.div
          variants={fade} initial="hidden" whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-16 flex flex-wrap gap-4"
        >
          <Link to="/opportunities/job"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full gradient-bg font-medium hover:shadow-[0_0_24px_rgba(157,76,221,0.5)] transition-shadow">
            Work with us <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 hover:border-white/35 text-white/80 hover:text-white transition-colors">
            Start a project together
          </Link>
        </motion.div>

      </div>
    </div>
  );
}
